import { useState, useEffect, useCallback } from 'react'
import {
  listGoals,
  createGoal,
  updateGoal,
  deleteGoal,
} from '../services/goalService'
import type { Goal } from '../types'

export function useGoals(householdId: string | null) {
  const [goals, setGoals]     = useState<Goal[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!householdId) return
    setLoading(true)
    setError(null)
    try {
      setGoals(await listGoals(householdId))
    } catch (err) {
      console.error('[useGoals] fetch error:', err)
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [householdId])

  useEffect(() => { fetch() }, [fetch])

  async function addGoal(
    payload: { name: string; target_amount: number; emoji?: string | null; deadline?: string | null },
  ): Promise<Goal> {
    if (!householdId) throw new Error('Sem household')
    const goal = await createGoal(householdId, payload)
    setGoals(prev => [...prev, goal])
    return goal
  }

  async function editGoal(
    id: string,
    payload: { name?: string; target_amount?: number; emoji?: string | null; deadline?: string | null },
  ): Promise<void> {
    await updateGoal(id, payload)
    await fetch()
  }

  async function removeGoal(id: string): Promise<void> {
    await deleteGoal(id)
    setGoals(prev => prev.filter(g => g.id !== id))
  }

  return { goals, loading, error, addGoal, editGoal, removeGoal, refetch: fetch }
}
